import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { partsNeededAPI } from '@shared/apiClient';

export default function PartsNeededFormModal({ isOpen, onClose, mode, part }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [received, setReceived] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(part?.name || '');
      setQuantity(part?.quantity || 1);
      setReceived(part?.received || false);
    }
  }, [isOpen, part]);

  const createMutation = useMutation(
    (payload) => partsNeededAPI.createPartNeeded(payload),
    {
      onSuccess: () => {
        toast.success('Part request created');
        queryClient.invalidateQueries('partsNeeded');
        queryClient.invalidateQueries('partsNeededSummary');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create part request');
      }
    }
  );

  const updateMutation = useMutation(
    ({ id, payload }) => partsNeededAPI.updatePartNeeded(id, payload),
    {
      onSuccess: () => {
        toast.success('Part request updated');
        queryClient.invalidateQueries('partsNeeded');
        queryClient.invalidateQueries('partsNeededSummary');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update part request');
      }
    }
  );

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!name.trim()) {
      toast.error('Part name is required');
      return;
    }

    const payload = {
      name: name.trim(),
      quantity: parseInt(quantity, 10) || 1
    };

    if (mode === 'edit') {
      updateMutation.mutate({
        id: part.id,
        payload: {
          ...payload,
          received
        }
      });
      return;
    }

    createMutation.mutate(payload);
  };

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {mode === 'edit' ? 'Update Part Request' : 'Request Part'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Part Name</label>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="input"
              placeholder="Ex: Battery charger"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              className="input"
              disabled={isSubmitting}
            />
          </div>

          {mode === 'edit' && (
            <div className="flex items-center space-x-2">
              <input
                id="received"
                type="checkbox"
                checked={received}
                onChange={(event) => setReceived(event.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                disabled={isSubmitting}
              />
              <label htmlFor="received" className="text-sm text-gray-700">
                Mark as received
              </label>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Update Request' : 'Request Part'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
